import React, { useState, useEffect, useCallback, useRef } from 'react';
import NotLinked from './components/NotLinked';
import Market from './components/Market';
import Bank from './components/Bank';
import Profile from './components/Profile';
import Navigation from './components/Navigation';
import Admin from './components/Admin';
import Sidebar from './components/Sidebar';
import PlayersList from './components/PlayersList';
import BankPanel from './components/BankPanel';
import MarketPanel from './components/MarketPanel';
import PlayerAvatar from './components/PlayerAvatar';
import AdminProf from './components/AdminProf';
import './styles.css';

// Ники, которые на самом деле являются ИИ, а не игроками.
// Значение — имя файла логотипа в папке public/logos/ (без расширения).
const AI_LOGOS = {
  Claude: 'claude',
  ChatGPT: 'chatgpt',
  Gemini: 'gemini',
};

// Куда вести из журнала действий по клику на #ID заявки: префикс типа действия →
// раздел админ-панели с этой заявкой. Раньше клик открывал историю операций
// счёта (чек товара) — теперь ведёт прямо к самой заявке в её разделе.
const OP_SECTION = {
  withdrawal_: 'bank-withdrawals',
  deposit_: 'bank-deposits',
  order_: 'market-orders',
  market_request_: 'market-requests',
};

// Плашка с аватаром/логотипом и ником в «рамочке».
function CreditBadge({ nick }) {
  const logo = AI_LOGOS[nick];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '5px 12px 5px 5px',
        borderRadius: '10px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        background: 'rgba(255, 255, 255, 0.06)',
        color: '#fff',
      }}
    >
      {logo ? (
        <img
          src={`${process.env.PUBLIC_URL}/logos/${logo}.png`}
          alt={nick}
          width={28}
          height={28}
          style={{ borderRadius: 6, objectFit: 'contain', background: '#fff' }}
        />
      ) : (
        <PlayerAvatar nickname={nick} size={28} rounded={6} />
      )}
      <span style={{ fontWeight: 600 }}>{nick}</span>
    </span>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('market');
  const [previousTab, setPreviousTab] = useState('market');
  const [tabNonce, setTabNonce] = useState(0);
  const [banInfo, setBanInfo] = useState(null);
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [appLoading, setAppLoading] = useState(true);
  // Гейт "первой полной загрузки приложения": пока false, поверх уже смонтированного
  // приложения держим ещё один спиннер-оверлей — уже после того, как syncAuth()
  // отработал, — пока не догрузится САМА первая открытая вкладка (Market/Bank/Profile).
  // Раньше между исчезновением экрана appLoading и появлением контента вкладки был
  // виден ещё один, отдельный экран "Загрузка..." самой вкладки — теперь они объединены
  // в один непрерывный спиннер. При переключении вкладок (не при первом входе) этот
  // гейт уже true и не участвует — там по-прежнему используется свой обычный
  // индивидуальный индикатор загрузки вкладки.
  const [firstTabReady, setFirstTabReady] = useState(false);
  const [notLinked, setNotLinked] = useState(false);
  const [syncError, setSyncError] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [roles, setRoles] = useState([]);
  const [adminInitialFocus, setAdminInitialFocus] = useState(null);
  const [adminRequestFocus, setAdminRequestFocus] = useState(null);
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('azon-theme');
      if (saved === 'light' || saved === 'dark') return saved;
    } catch (e) {}
    return (window.Telegram?.WebApp?.colorScheme === 'light') ? 'light' : 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('azon-theme', theme); } catch (e) {}
  }, [theme]);

  // Задаём фон-коллаж из public/ через JS, а не через CSS url('/...'),
  // т.к. сборщик CRA ломается на абсолютных путях внутри .css (см. styles.css).
  useEffect(() => {
    const base = process.env.PUBLIC_URL || '';
    document.documentElement.style.setProperty('--bg-image-light', `url(${base}/bg-light.png)`);
    document.documentElement.style.setProperty('--bg-image-dark', `url(${base}/bg-dark.png)`);
  }, []);

  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  // Повторный клик по уже открытой вкладке не меняет activeTab, поэтому React
  // ничего не перерисовывает — принудительно пересоздаём компонент вкладки
  // через nonce в key, чтобы она подгрузилась заново, как будто перешли из другой.
  const handleTabChange = (tab) => {
    if (tab === activeTab) {
      setTabNonce(n => n + 1);
    } else {
      setPreviousTab(activeTab);
      setActiveTab(tab);
    }
  };

  // Надпись "Azon" в шапке: одиночный клик (для суперадмина) по-прежнему открывает
  // обычную админ-панель, а ТРИ быстрых клика подряд — скрытую вкладку "Профиль
  // действий" (adminprof). Счётчик кликов с окном 400 мс различает эти два жеста.
  const azonClickRef = useRef({ count: 0, timer: null });
  const handleAzonClick = () => {
    if (!isSuperAdmin) return;
    const s = azonClickRef.current;
    s.count += 1;
    if (s.timer) { clearTimeout(s.timer); s.timer = null; }
    if (s.count >= 3) {
      s.count = 0;
      handleTabChange('adminprof');
      return;
    }
    s.timer = setTimeout(() => {
      if (s.count === 1) handleTabChange('admin');
      s.count = 0;
      s.timer = null;
    }, 400);
  };

  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  const tgId = tgUser?.id ? String(tgUser.id) : null;

  // Открыть заявку из «Профиля действий»: по клику на #ID определяем раздел
  // заявок по типу действия и перекидываем в админ-панель прямо к этой заявке
  // (с прокруткой и подсветкой). См. Admin.js -> requestFocus.
  const openOperationInAdmin = useCallback(({ type, id }) => {
    if (!tgId) return;
    const t = String(type || '');
    const prefix = Object.keys(OP_SECTION).find(p => t.indexOf(p) === 0);
    if (!prefix) return;
    setAdminRequestFocus({ section: OP_SECTION[prefix], requestId: String(id), nonce: Date.now() });
    setActiveTab('admin');
  }, [tgId]);

  const clearAdminFocus = useCallback(() => setAdminInitialFocus(null), []);
  const clearAdminRequestFocus = useCallback(() => setAdminRequestFocus(null), []);
  const tgUsername = tgUser?.username ? `@${tgUser.username}` : (tgUser?.first_name || 'Без имени');

  // Ссылка "поделиться товаром" открывает приложение с start_param вида item_123 —
  // если он есть, попросим вкладку Маркет открыть карточку этого товара сразу после загрузки.
  const [pendingItemId, setPendingItemId] = useState(() => {
    const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param || '';
    const match = /^item_(\d+)$/.exec(startParam);
    return match ? match[1] : null;
  });

  // Число активных заявок, требующих действия спец-роли (банкира/склада) —
  // показывается бейджем на пунктах "Банк"/"Маркетплейс" в боковом меню,
  // и виден только тем, у кого есть соответствующие права.
  const [bankAdminPendingCount, setBankAdminPendingCount] = useState(0);
  const [marketAdminPendingCount, setMarketAdminPendingCount] = useState(0);

  useEffect(() => {
    // Фикс бага браузера: прокрутка колесом мыши/трекпадом над сфокусированным
    // полем type="number" меняет его значение (по 1 за "щелчок" прокрутки).
    // Снимаем фокус с числового поля при прокрутке над ним, чтобы значение не менялось.
    const onWheel = (e) => {
      const el = document.activeElement;
      if (el && el.tagName === 'INPUT' && el.type === 'number') {
        el.blur();
      }
    };
    document.addEventListener('wheel', onWheel, { passive: true });
    return () => document.removeEventListener('wheel', onWheel);
  }, []);

  // Тянет актуальный никнейм Minecraft из MultiPunk API (через сервер) и логинит
  // пользователя. Вызывается один раз при открытии приложения, а также вручную
  // с экрана "привяжите аккаунт" по кнопке "проверить снова".
  const syncAuth = useCallback(async () => {
    if (!tgId) return;
    setSyncError(false);
    try {
      const res = await fetch('/api/auth/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId, tg_username: tgUsername })
      });
      const data = await res.json();

      if (res.status === 403) {
        if (data.isMaintenance) setIsMaintenance(true);
        else setBanInfo({ reason: data.ban_reason, bannedBy: data.banned_by });
        return;
      }

      if (res.status === 404 && data.error === 'NotLinked') {
        setNotLinked(true);
        setUser(null);
        return;
      }

      if (!res.ok) {
        setSyncError(true);
        return;
      }

      setNotLinked(false);
      try {
        const bankRes = await fetch(`/api/bank/${tgId}`);
        const bankData = await bankRes.json();
        setUser({ tgId, nickname: data.mc_nickname, balance: bankData.balance || 0 });
      } catch (e) {
        setUser({ tgId, nickname: data.mc_nickname, balance: 0 });
      }
      setActiveTab(prev => (data.isNew ? 'profile' : prev));
    } catch (err) {
      setSyncError(true);
    }
  }, [tgId, tgUsername]);

  useEffect(() => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
      if (window.Telegram.WebApp.disableVerticalSwipes) {
        window.Telegram.WebApp.disableVerticalSwipes();
      }
    }
    
    if (tgId) {
      syncAuth().finally(() => setAppLoading(false));
    } else {
      setAppLoading(false);
    }
    // syncAuth намеренно не в зависимостях: вызываем один раз за сессию при появлении tgId,
    // а не при каждом обновлении tgUsername (он входит в зависимости syncAuth).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tgId]);

  // Глобальный поллинг для проверки бана и обновления last_login
  useEffect(() => {
    if (tgId) {
      const interval = setInterval(() => {
        fetch('/api/auth/ping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tg_id: tgId, tg_username: tgUsername })
        }).then(async res => {
          if (res.status === 403) {
            const data = await res.json();
            if (data.isMaintenance) {
              setIsMaintenance(true);
            } else {
              setBanInfo({ reason: data.ban_reason, bannedBy: data.banned_by });
              setUser(null);
            }
          } else if (res.ok) {
            fetch(`/api/bank/${tgId}`).then(r => r.json()).then(bData => {
              setUser(prev => prev ? { ...prev, balance: bData.balance || 0 } : null);
            }).catch(e => console.error(e));
          }
        }).catch(e => console.error(e));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [tgId, tgUsername]);

  // Проверка прав администратора и ролей один раз при загрузке — используется
  // для показа нужных пунктов в боковом меню и прав внутри админ-панелей.
  useEffect(() => {
    if (tgId) {
      fetch(`/api/admin/check/${tgId}`)
        .then(res => res.json())
        .then(data => {
          setIsAdmin(!!data.isAdmin);
          setIsSuperAdmin(!!data.isSuperAdmin);
          setRoles(Array.isArray(data.roles) ? data.roles : []);
        })
        .catch(err => console.error('Ошибка проверки админа', err));
    }
  }, [tgId]);

  // Число активных заявок для бейджей в боковом меню "Банк"/"Маркетплейс" —
  // опрашиваем отдельно от самих панелей, т.к. Sidebar виден всегда, а
  // BankPanel/MarketPanel монтируются только пока открыта нужная вкладка.
  // Запрашиваем только те счётчики, на которые есть права — обычному
  // игроку без спец-роли эти эндпоинты не нужны.
  useEffect(() => {
    if (!tgId) return;
    const canManageBankNow = isAdmin || roles.includes('banker');
    const canManageWarehouseNow = isAdmin || roles.includes('warehouse');
    if (!canManageBankNow && !canManageWarehouseNow) return;
    const fetchCounts = () => {
      if (canManageBankNow) {
        fetch('/api/bank/admin-pending-count').then(r => r.json()).then(d => setBankAdminPendingCount(d.count || 0)).catch(() => {});
      }
      if (canManageWarehouseNow) {
        fetch('/api/market/admin-pending-count').then(r => r.json()).then(d => setMarketAdminPendingCount(d.count || 0)).catch(() => {});
      }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 2000);
    return () => clearInterval(interval);
  }, [tgId, isAdmin, roles]);

  if (appLoading) {
    return (
      <>
        <div className="bg-layer" />
        <div className="app-loading">
          <div className="spinner" />
          <p>Загрузка...</p>
        </div>
      </>
    );
  }

  if (!tgId) {
    return (
      <>
        <div className="bg-layer" />
        <div className="app-error">
          <h2>Ошибка доступа</h2>
          <p>Пожалуйста, откройте это приложение через Telegram.</p>
        </div>
      </>
    );
  }

  if (isMaintenance) {
    return (
      <>
        <div className="bg-layer" />
        <div className="banned-screen" style={{backgroundColor: 'var(--accent-soft)', color: 'var(--accent-dark)'}}>
          <div className="banned-icon">🛠</div>
          <h2>Технические работы</h2>
          <p>Приложение временно недоступно. Пожалуйста, зайдите позже.</p>
        </div>
      </>
    );
  }

  if (banInfo) {
    return (
      <>
        <div className="bg-layer" />
        <div className="banned-screen">
          <div className="banned-icon">🚫</div>
          <h2>Аккаунт заблокирован</h2>
          <p style={{marginBottom: '5px'}}><b>Причина:</b> {banInfo.reason || 'Не указана'}</p>
          <p style={{marginTop: 0}}><b>Заблокировал:</b> {banInfo.bannedBy || 'Администратор'}</p>
          <p style={{marginTop: '20px', fontSize: '14px', color: 'var(--text-secondary)'}}>Если возникли вопросы, обращайтесь в поддержку</p>
        </div>
      </>
    );
  }

  if (notLinked) {
    return (
      <>
        <div className="bg-layer" />
        <NotLinked onRetry={syncAuth} />
      </>
    );
  }

  if (syncError || !user) {
    return (
      <>
        <div className="bg-layer" />
        <div className="app-error">
          <h2>Не удалось подключиться</h2>
          <p>Не получилось проверить привязку аккаунта. Проверьте соединение и попробуйте ещё раз.</p>
          <button className="primary-btn" style={{marginTop: '15px'}} onClick={() => { setAppLoading(true); syncAuth().finally(() => setAppLoading(false)); }}>
            Попробовать снова
          </button>
        </div>
      </>
    );
  }

  const isModerator = roles.includes('moderator');
  const canManageBank = isAdmin || roles.includes('banker');
  const canManageWarehouse = isAdmin || roles.includes('warehouse');
  const canViewAdminPanel = isAdmin || isModerator;

  return (
    <div>
      {!firstTabReady && (
        <>
          <div className="bg-layer" />
          <div className="app-loading app-loading-overlay">
            <div className="spinner" />
            <p>Загрузка...</p>
          </div>
        </>
      )}
      <div className="bg-layer" />
      <header className="app-header">
        <div className="header-left">
          <button className="menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Открыть меню">
            <span></span>
            <span></span>
            <span></span>
          </button>
          <span 
            style={{userSelect: 'none', cursor: isSuperAdmin ? 'pointer' : 'default'}}
            onClick={handleAzonClick}
          >
            {activeTab === 'bank' ? 'Azon банк' : 'Azon'}
          </span>
        </div>
        {activeTab === 'market' ? (
          <span className="user-badge" style={{color: 'var(--warning)'}}>{user.balance !== undefined ? user.balance : 0} сц</span>
        ) : (
          <span className="user-badge">{user.nickname}</span>
        )}
      </header>

      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        nickname={user.nickname}
        canManageBank={canManageBank}
        canManageWarehouse={canManageWarehouse}
        canViewAdminPanel={canViewAdminPanel}
        bankPendingCount={bankAdminPendingCount}
        marketPendingCount={marketAdminPendingCount}
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        theme={theme}
        onToggleTheme={toggleTheme}
      />
      
      <main>
        {activeTab === 'market' && <Market key={`market-${tabNonce}`} tgId={tgId} pendingItemId={pendingItemId} onPendingItemHandled={() => setPendingItemId(null)} onInitialLoad={() => setFirstTabReady(true)} />}
        {activeTab === 'bank' && <Bank key={`bank-${tabNonce}`} tgId={tgId} setActiveTab={handleTabChange} onInitialLoad={() => setFirstTabReady(true)} />}
        {activeTab === 'profile' && <Profile key={`profile-${tabNonce}`} tgId={tgId} onUpdateNickname={(newNick) => setUser({...user, nickname: newNick})} onInitialLoad={() => setFirstTabReady(true)} />}
        {activeTab === 'playerslist' && <PlayersList key={`playerslist-${tabNonce}`} />}
        {activeTab === 'bank-admin' && canManageBank && <BankPanel key={`bank-admin-${tabNonce}`} tgId={tgId} adminNickname={user.nickname} canAct={canManageBank} />}
        {activeTab === 'market-admin' && canManageWarehouse && <MarketPanel key={`market-admin-${tabNonce}`} tgId={tgId} adminNickname={user.nickname} canAct={canManageWarehouse} />}
        {activeTab === 'admin' && canViewAdminPanel && (
          <Admin
            key={`admin-${tabNonce}`}
            tgId={tgId}
            adminNickname={user.nickname}
            isAdmin={isAdmin}
            isModerator={isModerator}
            canManageBank={canManageBank}
            canManageWarehouse={canManageWarehouse}
            initialFocus={adminInitialFocus}
            onFocusConsumed={clearAdminFocus}
            requestFocus={adminRequestFocus}
            onRequestFocusConsumed={clearAdminRequestFocus}
          />
        )}
        {activeTab === 'adminprof' && isSuperAdmin && (
          <AdminProf key={`adminprof-${tabNonce}`} tgId={tgId} onInitialLoad={() => setFirstTabReady(true)} onOpenOperation={openOperationInAdmin} />
        )}
        
        {activeTab === 'policy' && (
          <div className="card" style={{textAlign: 'center'}}>
            <h2>Пользовательское соглашение</h2>
            <p className="selectable-text" style={{color: 'var(--text-secondary)', textAlign: 'left', fontSize: '14px'}}>
              1. Принятие соглашения<br/>
              Нажимая кнопку «Принять и продолжить», а также в дальнейшем при использовании приложения игрок подтверждает, что ознакомлен и согласен со всеми пунктами пользовательского соглашения AZON.<br/><br/>

              2. Обязанности игрока<br/>
              При регистрации и использовании платформы игрок обязуется:<br/>
              - указать свой <b>настоящий</b> игровой ник, совпадающий с ником на сервере MultiPunk.<br/>
              - указать действующий адрес внутриигровой почты, используемой на сервере.<br/>
              - указать номер счёта, совпадающий с номером счёта на сервере MultiPunk.<br/>
              - соблюдать правила сервера MultiPunk при работе на маркетплейсе и при использовании банка.<br/>
              - <b>не использовать более одного аккаунта</b> AZON на одного человека.<br/>
              Указание недостоверных данных (чужого или несуществующего ника, почты, номера счёта), а также попытка привязать AZON к мультиаккаунту, является нарушением соглашения и <b>основанием для блокировки счёта</b>.<br/><br/>

              3. Данные, которые мы собираем и как их используем<br/>
              Для работы платформы собираются и хранятся:<br/>
              - игровой ник — для идентификации владельца счёта.<br/>
              - номер игрового счёта на сервере MultiPunk — для транзакций и вывода средств.<br/>
              - внутриигровая почта — используется для вывода средств.<br/>
              - данные из Telegram Mini App (Telegram ID и ник) — для привязки и авторизации.<br/>
              Данные используются исключительно для открытия и ведения счёта, проведения переводов и платежей, вывода средств, работы на маркетплейсе и отправки уведомлений через Telegram-бота.<br/><br/>

              4. Валюта платформы<br/>
              Единственной валютой, используемой на AZON, являются <b>самоцветы</b> — внутриигровая валюта сервера MultiPunk. AZON <b>не осуществляет обмен самоцветов на реальные деньги</b> и не принимает реальные деньги в качестве оплаты за внутриигровые услуги ни в каком виде.<br/>
              В соответствии с п. 1.6.1 Правил сервера MultiPunk обмен игровой валюты на реальные деньги (и наоборот) <b>строго запрещён</b>. AZON не участвует и не содействует подобным операциям. При выявлении фактов использования платформы для обмена самоцветов на реальные деньги счёт игрока <b>блокируется</b>, а информация передаётся администрации сервера MultiPunk для рассмотрения в рамках Правил сервера.<br/>
              Обмен самоцветов на реальные услуги (например рисунок) не запрещён Правилами сервера и допускается, однако AZON не выступает гарантом и не несёт ответственности за исполнение таких договорённостей между игроками.<br/><br/>

              5. Реквизиты и блокировка счёта<br/>
              При привязке или выводе средств игрок <b>обязан</b> указывать <b>СВОЙ</b> номер счёта на MultiPunk. Если указан чужой или несуществующий номер счёта, счёт <b>временно блокируется</b> до выяснения обстоятельств. Решение о разблокировке, возврате средств или окончательной блокировке (удалении) принимается администрацией платформы по итогам разбирательства.<br/><br/>

              6. Доступ через Telegram<br/>
              Счёт в AZON привязан к Telegram-аккаунту игрока. <b>Потеря доступа к Telegram означает потерю доступа к счёту.</b> Платформа не хранит альтернативных способов входа и не несёт ответственности за невозможность восстановить доступ при утрате аккаунта Telegram.<br/><br/>

              7. Инвойсы<br/>
              AZON предоставляет возможность выставления инвойса игроку:<br/>
              - по решению внутриигрового суда.<br/>
              - по требованию высших должностных лиц сервера (Президент, министры, Верховный судья).<br/>
              - по просьбе администрации сервера.<br/>
              Платформа AZON не устанавливает и не назначает собственные инвойсы, а лишь отражает решение, уже принятое судом, должностным лицом или администрацией в рамках правил сервера. AZON предоставляет только техническое удобство выставления и оплаты такого инвойса.<br/><br/>

              8. Бан на сервере<br/>
              В случае бана игрока на сервере MultiPunk его счётом распоряжается администрация сервера. Это касается как перманентного, так и временного бана.<br/>
              Если бан был снят, управление счётом возвращается игроку с разрешения администрации сервера.<br/><br/>

              9. Маркетплейс<br/>
              Сделки на маркетплейсе проводятся через счёт AZON Bank. Ответственность за качество и достоверность описания товара несёт продавец.<br/>
              Название и описание товара, выставленного на продажу, <b>обязаны полностью соответствовать действительности</b>. Попытка ввести покупателя в заблуждение — указание недостоверного названия, характеристик или количества товара — приводит к <b>изъятию товара и блокировке пользователя</b>.<br/>
              Запрещается выставлять на продажу предметы, полученные с нарушением Правил сервера, в том числе:<br/>
              - дублированные (дюп) предметы (п. 1.4 Правил сервера).<br/>
              - предметы, украденные у других игроков (п. 1.5.3).<br/>
              - предметы, полученные при помощи читов, багов или сторонних модификаций (п. 1.0, 1.0.2).<br/>
              Если будет установлено, что выставленный на продажу товар получен нечестным путём, сделка аннулируется, счёт продавца блокируется, а информация передаётся администрации сервера для рассмотрения в рамках Правил сервера.<br/>
              Споры между игроками по сделкам на маркетплейсе рассматриваются администрацией платформы на основании данных бота (истории сделок, переписки в рамках площадки). Если спор не может быть урегулирован администрацией платформы, он передаётся на рассмотрение внутриигрового суда сервера MultiPunk.<br/><br/>

              10. Чего мы НЕ делаем<br/>
              Мы <b>НЕ</b> запрашиваем реальные ФИО, паспортные данные, адреса, биометрию.<br/>
              Мы <b>НЕ</b> передаём данные игрока другим игрокам (кроме номера счёта, необходимого для переводов) или сторонним проектам.<br/>

              11. Хранение данных<br/>
              Данные хранятся в базе Telegram-бота до тех пор, пока используется платформа. При удалении аккаунта данные удаляются в течение <b>30 дней</b>, за исключением логов транзакций, которые хранятся отдельно для разбора спорных ситуаций.<br/>
              Резервные копии данных и балансов создаются регулярно.

              12. Права пользователя<br/>
              Игрок может <b>запросить список своих данных</b> или <b>потребовать удаления аккаунта</b> (кроме логов транзакций). Запрос рассматривается в течение <b>7 дней</b> с момента обращения.<br/><br/>

              13. Ограничение ответственности<br/>
              AZON не несёт ответственности за сбои игрового сервера MultiPunk и действия его администрации.<br/><br/>

              14. Разрешение споров<br/>
              Спорные ситуации, связанные с использованием AZON, в первую очередь рассматриваются администрацией платформы. Если спор не может быть урегулирован на этом уровне, либо затрагивает нарушение Правил сервера MultiPunk (кража, гриф, мошенничество, дюп и др.), он передаётся на рассмотрение внутриигрового суда сервера. Решение суда является обязательным для платформы AZON в части, касающейся судьбы счёта и средств вовлечённых игроков.<br/><br/>

              15. Рекламные баннеры и продвижение товара<br/>
              <b>Запрещено размещать на баннерах:</b><br/>
              - материалы 18+, эротического или порнографического характера.<br/>
              - контент, разжигающий межнациональную, расовую, религиозную или иную рознь, включая пропаганду нацизма, расизма, фашизма и шовинизма.<br/>
              - рекламу сторонних проектов, серверов и ресурсов, не связанных с MultiPunk и AZON (в соответствии с п. 2.1 Правил сервера).<br/>
              - пропаганду наркотиков, насилия или иных противоправных действий.<br/>
              - ложную или вводящую в заблуждение информацию.<br/>
              - слова и фразы, запрещённые на платформе Twitch (в соответствии с п. 2.2 Правил сервера).<br/>
              - личные данные третьих лиц без их согласия, в том числе имена, фотографии, контакты.<br/>
              При обнаружении нарушения баннер удаляется <b>без возврата</b> уплаченных за размещение средств, а игроку выносится предупреждение либо временная блокировка — решение принимает администрация платформы в зависимости от тяжести нарушения.<br/><br/>
              <b>Продвижение товара</b><br/>
              Продвижение товара на маркетплейсе осуществляется исключительно текстовым описанием, без использования изображений. В тексте продвижения запрещено:<br/>
              - использовать нецензурную лексику, оскорбления и слова, запрещённые на платформе Twitch (в соответствии с п. 2.2 Правил сервера).<br/>
              - указывать заведомо ложную информацию о товаре, его характеристиках или происхождении.<br/>
              - размещать ссылки на сторонние проекты, серверы или ресурсы (в соответствии с п. 2.1 Правил сервера).<br/>
              - разжигать межнациональную, расовую, религиозную или иную рознь, включая пропаганду нацизма, расизма, фашизма и шовинизма.<br/>
              - размещать спам, повторяющийся или бессмысленный текст, а также текст, набранный капсом.<br/>
              При обнаружении нарушения продвижение товара отменяется <b>без возврата</b> уплаченных средств, а игроку выносится предупреждение либо временная блокировка — решение принимает администрация платформы в зависимости от тяжести нарушения.<br/><br/>

              16. Контакты<br/>
              По всем вопросам, связанным с работой платформы, обработкой данных, спорными ситуациями или запросами на удаление аккаунта, обращайтесь: <b>@zolartem</b>.
            </p>

            <button className="primary-btn" onClick={() => setActiveTab(previousTab)} style={{marginTop: '15px'}}>Назад</button>
          </div>
        )}


        {activeTab === 'about' && (
          <div className="card" style={{textAlign: 'center'}}>
            <h2>О приложении</h2>
            <p className="selectable-text" style={{color: 'var(--text-secondary)', textAlign: 'left', fontSize: '14px'}}>
              <b>AZON</b> — платформа на сервере MultiPunk, объединяющая в себе банк и маркетплейс. Платформа создана игроками для игроков и призвана упростить внутриигровую торговлю и денежные расчёты между участниками сервера.
            </p>

            <h3 style={{textAlign: 'left', marginTop: '20px', marginBottom: '8px'}}>AZON Bank</h3>
            <p className="selectable-text" style={{color: 'var(--text-secondary)', textAlign: 'left', fontSize: '14px'}}>
              Банковский модуль платформы, через который игроки могут:<br/>
              - хранить внутриигровую валюту на личном счёте.<br/>
              - создавать карты для удобных расчётов.<br/>
              - переводить деньги другим игрокам мгновенно, без необходимости входа на сервер.<br/>
              - получать деньги от других игроков и отслеживать историю поступлений.<br/>
              - получать уведомления через Telegram-бота.<br/>
              - кастомизировать собственную карту.<br/>
              Банк работает как надстройка над внутриигровой экономикой сервера MultiPunk и не связан с реальными деньгами, банковскими картами или платёжными системами.
            </p>

            <h3 style={{textAlign: 'left', marginTop: '20px', marginBottom: '8px'}}>AZON Маркетплейс</h3>
            <p className="selectable-text" style={{color: 'var(--text-secondary)', textAlign: 'left', fontSize: '14px'}}>
              Торговая площадка платформы, где игроки могут:<br/>
              - продавать любые товары, существующие в Minecraft, а также кастомные блоки MP SMP  — от блоков до редких предметов.<br/>
              - продвигать свои товары среди других игроков, повышая их видимость на площадке.<br/>
              - покупать баннер для размещения товара или объявления на видном месте маркетплейса.<br/>
              - рассчитываться за покупки напрямую через счёт AZON Bank, без необходимости отдельного перевода.<br/>
              Маркетплейс и банк работают в связке: расчёты за сделки на площадке проходят через банковские счета пользователей, что снижает риск обмана при обмене товарами.
            </p>

            <div className="selectable-text" style={{textAlign: 'left', marginTop: '20px', color: 'var(--text-secondary)', fontSize: '14px'}}>
              <b>Дата запуска:</b> 23.08.2026<br/>
              <b>Версия:</b> 1.0.0-Re
            </div>

            <div style={{textAlign: 'left', margin: '15px 0'}}>
              <button className="link-btn" onClick={() => handleTabChange('policy')} style={{display: 'block'}}>
                Пользовательское соглашение
              </button>
            </div>

            <div style={{textAlign: 'left', marginTop: '20px'}}>
              <h3 style={{marginBottom: '12px'}}>Создатели</h3>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
                {['LuckySRider', 'Movdig', 'StoredGerakl885'].map(nick => (
                    <CreditBadge key={nick} nick={nick} />
                ))}
              </div>
            </div>

            <div style={{textAlign: 'left', marginTop: '20px'}}>
              <h3 style={{marginBottom: '12px'}}>Особая благодарность</h3>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
                {['anokss', 'Mih4n', 'WryFANTOM2020', 'Beta6678422', 'Claude', 'ChatGPT', 'Gemini'].map(nick => (
                    <CreditBadge key={nick} nick={nick} />
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      <Navigation activeTab={activeTab} setActiveTab={handleTabChange} />
    </div>
  );
}

export default App;